import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { useQuery } from 'react-query';
import { IBankAccountViewModel } from 'viewModels';
import { WalletController } from '../controller/WalletController';
import { Bank, GetAllBanks } from '../services/api/banks';
import { useAuth } from './auth';
import { TypeUser } from '../types/enums';

type BankAccountsProps = {
  children: ReactNode;
};

type IInitialValues = {
  bankAccounts: IBankAccountViewModel[];
  setBankAccounts: React.Dispatch<React.SetStateAction<IBankAccountViewModel[]>>;
  listBanks: Bank[];
  returnNameBank: (id: string) => string;
};

const BankAccountContext = createContext<IInitialValues>({} as IInitialValues);

const BankAccountProvider = ({ children }: BankAccountsProps) => {
  const { user, tokenIsValid } = useAuth();
  const [bankAccounts, setBankAccounts] = useState<IBankAccountViewModel[]>([]);
  const [listBanks, setListBanks] = useState<Bank[]>([]);

  const returnNameBank = useCallback(
    (id: string) => {
      const bank = listBanks.find((b) => b.code === Number(id));
      return bank?.name ?? '--';
    },
    [listBanks],
  );

  useQuery('banks', () => GetAllBanks(), {
    onSuccess: (data) => {
      setListBanks(data?.sort((a, b) => (a.name === b.name ? 0 : a.name ? -1 : 1)) ?? []);
    },
    refetchOnWindowFocus: false,
    enabled: !!user.id && tokenIsValid,
  });

  useQuery(
    'banksAccounts',
    () =>
      WalletController.getAllBankAccounts(
        user.type === TypeUser.COLLABORATOR ? user.companyId ?? '' : user.id,
      ),
    {
      onSuccess: (data) => {
        const newResults = data
          .sort((a, b) => (a.isPreferred === b.isPreferred ? 0 : a.isPreferred ? -1 : 1))
          .map((bank) => ({
            ...bank,
            nameBank: returnNameBank(bank.bank),
          }));
        setBankAccounts(newResults);
      },
      refetchOnWindowFocus: false,
      enabled:
        (user.type === TypeUser.COLLABORATOR ? !!user?.companyId : !!user.id) && tokenIsValid,
    },
  );

  useEffect(() => {
    if (!tokenIsValid) {
      setBankAccounts([]);
      setListBanks([]);
    }
  }, [tokenIsValid]);

  const contextValue = useMemo(
    () => ({ bankAccounts, setBankAccounts, listBanks, returnNameBank }),
    [bankAccounts, listBanks, returnNameBank],
  );
  return <BankAccountContext.Provider value={contextValue}>{children}</BankAccountContext.Provider>;
};

export default BankAccountProvider;

export const useBankAccounts = () => useContext(BankAccountContext);
